import { Alert, Button, Tag } from "antd";
import { ClockCircleOutlined, FireOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { canExtendPayment } from "./ExtendPaymentDialog";

/**
 * Hạn giao dịch đã gia hạn (payment_exp_date), chỉ khi muộn hơn ngày kết thúc.
 * Gia hạn không đổi end_date/due_date (CollectionService::extendPaymentExpiry) nên màn nào
 * hiện hạn cũng phải hiện thêm mốc này, không thì trông như chưa gia hạn.
 */
export const paymentExtendedUntil = (collection) => {
  if (!collection?.payment_exp_date) return null;
  const extended = dayjs(collection.payment_exp_date);
  if (collection.end_date && !extended.isAfter(dayjs(collection.end_date), "day")) return null;
  return extended;
};

/** Chỉ khoản CHƯA THANH TOÁN được cấp lại mã hạn mới; khoản PARTIAL giữ mã cũ. */
export const feePaymentExtendedUntil = (fee) =>
  fee?.status === "UNPAID" ? paymentExtendedUntil(fee.collection) : null;

/** Tag hạn thu cạnh tên đợt: quá hạn N ngày / còn ≤ 7 ngày. Đợt đã kết thúc thì không hiện. */
export const DeadlineTag = ({ collection }) => {
  if (!collection?.end_date || collection.status === "CLOSED") return null;

  const daysLeft = dayjs(collection.end_date).diff(dayjs(), "day");
  const extended = paymentExtendedUntil(collection);

  if (daysLeft < 0) {
    return (
      <>
        <Tag icon={<FireOutlined />} color="error" style={{ fontWeight: 600 }}>
          Quá hạn {Math.abs(daysLeft)} ngày
        </Tag>
        {extended && (
          <Tag icon={<ClockCircleOutlined />} color="processing" style={{ fontWeight: 600 }}>
            Đã gia hạn đến {extended.format("DD/MM/YYYY")}
          </Tag>
        )}
      </>
    );
  }
  if (daysLeft <= 3) {
    return (
      <Tag icon={<WarningOutlined />} color="error" style={{ fontWeight: 600 }}>
        Còn {daysLeft} ngày
      </Tag>
    );
  }
  if (daysLeft <= 7) {
    return (
      <Tag icon={<ClockCircleOutlined />} color="warning" style={{ fontWeight: 600 }}>
        Còn {daysLeft} ngày
      </Tag>
    );
  }
  return null;
};

/**
 * Khung báo đợt quá hạn kèm nút gia hạn — tách khỏi hàng nút thao tác thường ngày vì đây
 * là việc xử lý sự cố, người dùng cần biết VÌ SAO phải bấm.
 *
 * Ai gia hạn được (khớp CollectionService::extendPaymentExpiry + validateUpdatePermission):
 *  - Chủ đợt thu: đợt cha (áp cho mọi đợt con cùng một hạn) hoặc đợt đứng một mình.
 *  - Nhân sự (STAFF/BRANCH_MANAGER): chỉ đợt đứng một mình thuộc đơn vị mình — chính là đợt
 *    nhân sự tự tạo (storeParent không sinh đợt cha cho nhân sự). Không gia hạn đợt con riêng
 *    → các đợt con của một đợt cha luôn cùng một hạn, không lệch nhau.
 *
 * Lưu ý: đợt đứng một mình cũng mang type_child = "PARENT". Phân biệt bằng organization_id:
 * đợt cha thật không gắn đơn vị, đợt đứng một mình thì có.
 * Đợt con vẫn hiện khung (nhân sự là người gửi lại tin) nhưng không có nút, chỉ hướng dẫn.
 */
export const OverduePaymentAlert = ({ collection, onExtend }) => {
  const { user } = useSelector((state) => state.persistedReducer.user);
  const userType = user?.data?.info?.type;
  const isStaffScopedUser = userType === "STAFF" || userType === "BRANCH_MANAGER";

  if (
    collection?.status !== "ONGOING" ||
    !collection.end_date ||
    !dayjs(collection.end_date).isBefore(dayjs(), "day")
  ) {
    return null;
  }

  // Đợt con mang cùng payment_exp_date với đợt cha (backend ghi cho cả cụm).
  const extended = collection.payment_exp_date && !dayjs(collection.payment_exp_date).isBefore(dayjs(), "day");
  const isLocked = collection.is_locked === true || collection.is_locked === 1;
  const canAct =
    canExtendPayment(collection) &&
    (!isStaffScopedUser || (!!collection.organization_id && !isLocked));

  return (
    <Alert
      type={extended ? "info" : "warning"}
      showIcon
      className="mb-4"
      message={
        extended
          ? `Đã gia hạn giao dịch thanh toán đến ${dayjs(collection.payment_exp_date).format("DD/MM/YYYY")}.`
          : "Đợt thu đã quá hạn. Mã thanh toán của các khoản chưa nộp đã hết hạn, gửi lại tin hoặc in phiếu sẽ dùng mã cũ."
      }
      description={
        canAct
          ? null
          : collection.parent_id
            ? "Gia hạn giao dịch được thực hiện ở đợt cha, áp dụng chung cho tất cả đơn vị."
            : "Liên hệ chủ đợt thu để gia hạn giao dịch thanh toán."
      }
      action={
        canAct && (
          <Button size="small" icon={<ClockCircleOutlined />} onClick={onExtend}>
            {extended ? "Gia hạn thêm" : "Gia hạn giao dịch thanh toán"}
          </Button>
        )
      }
    />
  );
};
